import React, { useEffect } from 'react';

import { Center, Flex, FlexProps, Loader, Text, useMantineTheme } from '@mantine/core';
import { IconCheck, IconX } from '@tabler/icons-react';
import { useMutation } from '@tanstack/react-query';
import { ChatActionButton } from 'components/SmartAssistantComponents/ChatActionButton';
import { LiveAudioVisualizer } from 'components/SmartAssistantComponents/LiveAudioVisualizer';
import { useAudioRecorder } from 'react-audio-voice-recorder';
import { useTranslation } from 'react-i18next';
import { GuestSmartAssistantService } from 'services/guest-smart-assistant-service';
import { SmartAssistantService } from 'services/smart-assistant-service';
import { SmartAssistantVisualConfig } from 'shared/interfaces/SmartAssistant';
import { formatSecondsToMinutesAndSeconds } from 'shared/utils/date';

const MAX_RECORDING_TIME_SECONDS = 180;

interface SmartAssistantChatAudioAttachmentHandlerProps extends FlexProps {
  smartAssistantId: string;
  setIsRecording: (isRecording: boolean) => void;
  setIsMicrophoneDisabled: (isMicrophoneDisabled: boolean) => void;
  onTranscription: (transcription: string) => void;
  smartAssistantVisualConfig: SmartAssistantVisualConfig;
  setErrorMessage: (errorMessage: string | null) => void;
  isGuest?: boolean;
}

export const SmartAssistantChatAttachmentAudioHandler = ({
  smartAssistantId,
  setIsRecording,
  setIsMicrophoneDisabled,
  onTranscription,
  smartAssistantVisualConfig,
  isGuest = false,
  setErrorMessage,
  ...flexProps
}: SmartAssistantChatAudioAttachmentHandlerProps) => {
  const { t } = useTranslation();
  const theme = useMantineTheme();

  const audioBarColor = theme.other.getReadableColor(smartAssistantVisualConfig.conversationBackgroundColor);
  const textColor = theme.other.getReadableColor(smartAssistantVisualConfig.conversationBackgroundColor);

  const { startRecording, stopRecording, recordingBlob, isRecording, recordingTime, mediaRecorder } = useAudioRecorder(
    undefined,
    () => {
      setIsRecording(false);
      setIsMicrophoneDisabled(true);
    }
  );

  const transcribeAudioAttachmentMutation = useMutation({
    mutationFn: (isGuest ? GuestSmartAssistantService : SmartAssistantService).transcribeAudioAttachment,
    onSuccess: (data) => {
      onTranscription(data.transcription);
      setIsRecording(false);
    },
    onError: () => {
      setErrorMessage(t('FEED_BACK.SMART_ASSISTANT_INPUT.FAILURE.AUDIO_TRANSCRIPTION_ERROR'));
      setIsRecording(false);
    }
  });

  // Start recording automatically on mount
  useEffect(() => {
    const isMediaDeviceSupported =
      typeof navigator !== 'undefined' &&
      navigator.mediaDevices &&
      typeof navigator.mediaDevices.getUserMedia === 'function';

    if (isMediaDeviceSupported) startRecording();
    else {
      setIsRecording(false);
      setIsMicrophoneDisabled(true);
    }
  }, []);

  // Handle recording complete
  useEffect(() => {
    if (recordingBlob) {
      const extension = recordingBlob.type.split('/')[1] || 'webm';

      const audioFile = new File([recordingBlob], `audio-recording.${extension}`, {
        type: recordingBlob.type,
        lastModified: Date.now()
      });

      transcribeAudioAttachmentMutation.mutate({ file: audioFile, smartAssistantId: smartAssistantId });
    }
  }, [recordingBlob]);

  useEffect(() => {
    if (recordingTime >= MAX_RECORDING_TIME_SECONDS) {
      stopRecording();
    }
  }, [recordingTime, stopRecording]);

  const onCancelRecording = () => {
    setIsRecording(false);
    stopRecording();
  };

  return (
    <Flex align="center" justify="space-around" w="100%" gap={10} {...flexProps}>
      {!transcribeAudioAttachmentMutation.isLoading && (
        <ChatActionButton
          bg={smartAssistantVisualConfig.conversationBackgroundColor}
          onClick={onCancelRecording}
          disabled={!isRecording}>
          <IconX style={{ width: 24, height: 24 }} color={textColor} />
        </ChatActionButton>
      )}

      {mediaRecorder ? (
        <LiveAudioVisualizer mediaRecorder={mediaRecorder} w="100%" height={38} barColor={audioBarColor} />
      ) : (
        <Flex w="100%" h={38} bg={smartAssistantVisualConfig.conversationBackgroundColor} />
      )}

      {!transcribeAudioAttachmentMutation.isLoading && (
        <Text color={textColor} size="sm">
          {formatSecondsToMinutesAndSeconds(recordingTime)}
        </Text>
      )}

      {transcribeAudioAttachmentMutation.isLoading ? (
        <Center w={37} h={30} mr={8}>
          <Loader size={18} color={textColor} />
        </Center>
      ) : (
        <ChatActionButton
          mr={8}
          bg={smartAssistantVisualConfig.conversationBackgroundColor}
          onClick={stopRecording}
          disabled={!isRecording}>
          <IconCheck style={{ width: 24, height: 24 }} color={textColor} />
        </ChatActionButton>
      )}
    </Flex>
  );
};
